import React from 'react';
import DefaultContent from '../components/content/DefaultContent';
import JobDescriptionContent from '../components/content/JobDescriptionContent';
import InterviewQuestionsContent from '../components/content/InterviewQuestionsContent';
import ReportsContent from '../components/content/ReportsContent';
import VoiceChatContent from '../components/content/VoiceChatContent';
import ResumeScreeningContent from '../components/content/ResumeScreeningContent';
import ContentIdentificationService from './contentIdentificationService';

// New content block structure with React components
export interface ContentBlock {
  id: string;
  title: string;
  component: React.ComponentType<ContentComponentProps>;
  keywords: string[];
  description?: string;
}

export interface ContentComponentProps {
  candidate?: any;
  candidateId?: string;
  onContentChange?: (contentId: string) => void;
  onActionClick?: (action: string, data?: any) => void;
}

// Content block definitions
const defaultContent: ContentBlock = {
  id: 'default',
  title: 'AI Agent Capabilities',
  component: DefaultContent,
  keywords: ['default', 'help', 'capabilities', 'what can you do', 'welcome'],
  description: 'Overview of AI assistant capabilities',
};

const jobDescriptionContent: ContentBlock = {
  id: 'job-description',
  title: 'Job Description Tools',
  component: JobDescriptionContent,
  keywords: [
    'job description',
    'jd',
    'job',
    'analyze job',
    'job analysis',
    'summarize job',
    'requirements',
    'skills',
    'qualifications',
    'job requirements',
    'position',
    'role description',
    'job posting',
    'extract skills',
  ],
  description: 'Tools for analyzing job descriptions',
};

const interviewQuestionsContent: ContentBlock = {
  id: 'interview-questions',
  title: 'Interview Question Generator',
  component: InterviewQuestionsContent,
  keywords: [
    'interview',
    'questions',
    'interview questions',
    'generate questions',
    'question generator',
    'behavioral',
    'technical',
    'assessment',
    'interviewing',
    'ask',
    'screening questions',
    'prepare interview',
  ],
  description: 'Generate interview questions',
};

const reportsContent: ContentBlock = {
  id: 'reports',
  title: 'Report Generation',
  component: ReportsContent,
  keywords: [
    'report',
    'reports',
    'generate report',
    'create report',
    'analytics',
    'metrics',
    'dashboard',
    'candidate report',
    'statistics',
    'data',
    'insights',
    'analysis',
    'reporting',
    'export',
  ],
  description: 'Generate comprehensive reports',
};

const voiceChatContent: ContentBlock = {
  id: 'voice-chat',
  title: 'Voice Chat Assistant',
  component: VoiceChatContent,
  keywords: [
    'voice',
    'voice chat',
    'speak',
    'microphone',
    'audio',
    'speech',
    'talk',
    'voice mode',
    'voice assistant',
    'speech recognition',
    'voice command',
    'hands free',
  ],
  description: 'Voice-enabled AI assistant',
};

const resumeScreeningContent: ContentBlock = {
  id: 'resume-screening',
  title: 'Resume Screening Tools',
  component: ResumeScreeningContent,
  keywords: [
    'resume',
    'resumes',
    'cv',
    'screen',
    'screening',
    'candidate',
    'candidates',
    'match',
    'matching',
    'analyze resume',
    'resume analysis',
    'profile',
    'candidate profile',
    'parse resume',
    'resume parser',
    'evaluate',
  ],
  description: 'Advanced resume screening',
};

// All available content blocks
export const contentBlocks: ContentBlock[] = [
  defaultContent,
  jobDescriptionContent,
  interviewQuestionsContent,
  reportsContent,
  voiceChatContent,
  resumeScreeningContent,
];

/**
 * Find content based on keywords (local fallback method)
 * Used when API is unavailable or for testing
 */
export const findContentByKeywords = (message: string): ContentBlock => {
  if (!message || message.trim() === '') {
    return defaultContent;
  }

  const lowerMessage = message.toLowerCase().trim();
  
  // Score-based matching for better accuracy
  interface ScoredBlock {
    block: ContentBlock;
    score: number;
    matchedKeywords: string[];
  }

  const scoredBlocks: ScoredBlock[] = contentBlocks
    .map(block => {
      const matchedKeywords: string[] = [];
      let score = 0;

      block.keywords.forEach(keyword => {
        const lowerKeyword = keyword.toLowerCase();
        
        // Exact match (highest score)
        if (lowerMessage === lowerKeyword) {
          score += 100;
          matchedKeywords.push(keyword);
        }
        // Starts with keyword
        else if (lowerMessage.startsWith(lowerKeyword)) {
          score += 50;
          matchedKeywords.push(keyword);
        }
        // Contains full keyword
        else if (lowerMessage.includes(lowerKeyword)) {
          score += 30;
          matchedKeywords.push(keyword);
        }
        // Partial word match (check if keyword words are in message)
        else {
          const keywordWords = lowerKeyword.split(/\s+/);
          const messageWords = lowerMessage.split(/\s+/);
          const matchingWords = keywordWords.filter(kw => 
            messageWords.some(mw => mw.includes(kw) || kw.includes(mw))
          );
          
          if (matchingWords.length > 0) {
            score += matchingWords.length * 10;
            matchedKeywords.push(keyword);
          }
        }
      });

      return { block, score, matchedKeywords };
    })
    .filter(item => item.score > 0)
    .sort((a, b) => b.score - a.score);

  if (scoredBlocks.length > 0) {
    const bestMatch = scoredBlocks[0];
    console.log(`[ContentManager] Keyword match: "${message}" → "${bestMatch.block.title}" (score: ${bestMatch.score}, keywords: ${bestMatch.matchedKeywords.join(', ')})`);
    return bestMatch.block;
  }

  console.log(`[ContentManager] No keyword match found for "${message}", showing default content`);
  return defaultContent;
};

/**
 * Find content using API service only
 * Returns default content if API fails
 */
export const findContentByMessage = async (
  message: string,
  candidateContext?: any
): Promise<ContentBlock> => {
  if (!message || message.trim() === '') {
    return defaultContent;
  }

  try {
    console.log('[ContentManager] Calling API to identify content for:', message);

    // Get available content IDs for the API
    const availableContents = contentBlocks.map(block => block.id);

    // Call the content identification service (returns content_id as string)
    const contentId = await ContentIdentificationService.identifyContent(
      message,
      availableContents,
      candidateContext
    );

    console.log('[ContentManager] API response - content_id:', contentId);

    // Find the content block by ID
    const content = getContentById(contentId);

    if (content.id === 'default' && contentId !== 'default') {
      console.warn(`[ContentManager] API returned unknown content_id: "${contentId}", showing default content`);
      return defaultContent;
    }

    console.log(`[ContentManager] API match: "${message}" → "${content.title}"`);
    
    // Return the API-identified content
    return content;

  } catch (error) {
    console.error('[ContentManager] API identification failed:', error);
    // Return default content on error (no keyword fallback)
    console.log('[ContentManager] Showing default content due to API error');
    return defaultContent;
  }
};

// Function to get content by ID
export const getContentById = (id: string): ContentBlock => {
  return contentBlocks.find(block => block.id === id) || defaultContent;
};

// Function to get all available content options
export const getAllContentOptions = (): Array<{id: string, title: string, keywords: string[]}> => {
  return contentBlocks.map(block => ({
    id: block.id,
    title: block.title,
    keywords: block.keywords,
  }));
};
